import { objectBrowserStoreSelector, useObjectBrowserStore } from '@warehouse/object-browser/domain';
import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useSnackbar } from 'notistack';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ViewListIcon from '@mui/icons-material/ViewList';
import { IconButton } from '@mui/material';
import { MonacoEditor } from '../../../shared/ui/MonacoEditor';
import NxLoader from '../../../../src/components/library/NxLoader';
import { MediaInfoDetailed } from './MediaInfoDetailed';

const Container = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	height: 'calc(100vh - 128px)',
	padding: theme.spacing(2.5, 0),
	boxSizing: 'border-box',
	overflowY: 'hidden',
}));

const EmptyMessage = styled('div')({
	color: '#000000',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	height: '100%',
	fontSize: '18px',
	textAlign: 'center',
});

const InvalidMessage = styled('div')({
	color: '#ff0000',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	height: '100%',
	fontSize: '18px',
	textAlign: 'center',
});

const LoaderWrapper = styled('div')({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	height: '100%',
});

const Header = styled('div')(({ theme }) => ({
	position: 'absolute',
	justifyContent: 'flex-start',
	padding: theme.spacing(0, 2.5),
}));

const ComponentWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	minHeight: 0,
});

const EditorContainerWrapper = styled('div')(({ theme }) =>({
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	minHeight: 0,
	paddingleft: theme.spacing(0, 3),
}));

export function MediaInfoTab() {
	const { enqueueSnackbar } = useSnackbar();
    const [activeView, setActiveView] = useState<'detailed' | 'json'>('detailed');

	const selectedNxObjectFull = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObjectFull);
	const eTag =
		selectedNxObjectFull?.nxObject && 'eTag' in selectedNxObjectFull.nxObject
			? selectedNxObjectFull.nxObject.eTag
			: undefined;

	const mediaInfo = selectedNxObjectFull?.metadata?.mediaInfo ?? '';

	const { content, isInvalid } = useMemo(() => {
		if (!mediaInfo) {
			return {
				content: undefined,
				isInvalid: false,
			};
		}

		try {
			return {
				content: JSON.stringify(JSON.parse(mediaInfo), null, 2),
				isInvalid: false,
			};
		} catch (error) {
			return {
				content: undefined,
				isInvalid: true,
			};
		}
	}, [mediaInfo]);

    const actions = [
        {
            key: 'detailed',
            icon: <ViewListIcon aria-label="Detailed" />,
            onClick: () => setActiveView('detailed'),
            component: <MediaInfoDetailed mediaInfo={mediaInfo} />,
        },
        {
            key: 'json',
            icon: <DataObjectIcon aria-label="JSON" />,
            onClick: () => setActiveView('json'),
            component: 
				<EditorContainerWrapper>
					<MonacoEditor
						content={content}
						language="json"
						editModeAvailability={false}
						showDownloadButton
						title={eTag}
					/>
				</EditorContainerWrapper>,
        },
    ];

	useEffect(() => {
		if (isInvalid) {
			enqueueSnackbar('Failed to load MediaInfo file', { variant: 'error', preventDuplicate: true });
		}
	}, [isInvalid, enqueueSnackbar]);

	if (!selectedNxObjectFull) {
		return (
			<Container>
				<LoaderWrapper>
					<NxLoader size={60} />
				</LoaderWrapper>
			</Container>
		);
	}

	if (isInvalid) {
		return (
			<Container>
				<InvalidMessage>The MediaInfo file is invalid</InvalidMessage>
			</Container>
		);
	}

	if (mediaInfo === '') {
		return (
			<Container>
				<EmptyMessage>No MediaInfo available</EmptyMessage>
			</Container>
		);
	}

    const activeAction = actions.find((a) => a.key === activeView);

	return (
		<Container>
			<Header>
                {actions.map((action) => (
                    <IconButton
                        key={action.key}
                        onClick={action.onClick}
                        sx={{
                            padding: '6px',
                            color: activeView === action.key ? '#4C95DB' : 'action.disabled',
							backgroundColor: activeView === action.key ? '#E3F3FD' : 'transparent',
							'&:hover': {
								backgroundColor: activeView === action.key ? '#E3F3FD' : 'action.hover',
							},
                        }}
                    >
                        {action.icon}
                    </IconButton>
                ))}
            </Header>
            <ComponentWrapper>
                {activeAction?.component}
            </ComponentWrapper>
		</Container>
	);
}
